import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  listFamilyMembers, createFamilyMember, updateFamilyMember, deleteFamilyMember,
} from '@/api/familyMember';
import type { FamilyMemberPayload } from '@/api/familyMember';
import { validatePhone, validateEmail } from '@/utils/validation';

function FieldError({ msg }: { msg: string }) {
  return msg ? <p className="text-red-500 text-xs mt-0.5">{msg}</p> : null;
}

const RELATIONSHIPS = [
  'Head', 'Spouse', 'Son', 'Daughter', 'Father', 'Mother',
  'Brother', 'Sister', 'Grandfather', 'Grandmother', 'Grandson', 'Granddaughter',
  'Father-in-law', 'Mother-in-law', 'Son-in-law', 'Daughter-in-law', 'Brother-in-law', 'Sister-in-law',
  'Uncle', 'Aunt', 'Nephew', 'Niece', 'Cousin', 'Other',
];
const GENDERS       = ['Male', 'Female'];
const STATUSES      = ['Active', 'Deceased', 'Inactive'];
const BLOOD_GROUPS  = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const pick = (row: Record<string, unknown> | undefined, keys: string[]): string => {
  if (!row) return '';
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null) return String(v);
  }
  return '';
};
const pickBool = (row: Record<string, unknown> | undefined, keys: string[]): boolean => {
  if (!row) return false;
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null) return v === true || v === 1 || v === '1' || v === 'true';
  }
  return false;
};

const ACTIVITY_FIELDS: { key: keyof FamilyMemberPayload; label: string; rowKeys: string[] }[] = [
  { key: 'isAdmin',            label: 'Admin Council',     rowKeys: ['isAdmin', 'isadmin'] },
  { key: 'legionOfMary',       label: 'Legion of Mary',    rowKeys: ['isLOM', 'legionofmary'] },
  { key: 'isVincentDePaul',    label: 'Vincent de Paul',   rowKeys: ['isVDPaul', 'isvencentdepaul'] },
  { key: 'isChoir',            label: 'Choir',             rowKeys: ['isChoir', 'ischoir'] },
  { key: 'isCatechismTeacher', label: 'Catechism Teacher', rowKeys: ['isCatechismTeacher', 'iscatechismteacher'] },
  { key: 'isWomenAssoc',       label: "Women's Association", rowKeys: ['isWomenAssoc', 'iswomenassoc'] },
  { key: 'isLiturgyCouncil',   label: 'Liturgy Council',   rowKeys: ['isLitergyCouncil', 'islitergycouncil'] },
  { key: 'isAlterServices',    label: 'Altar Services',    rowKeys: ['isAlterServices', 'isalterservices'] },
  { key: 'isYouth',            label: 'Youth',             rowKeys: ['isYouth', 'isyouth'] },
];

export default function FamilyMemberFormPage() {
  const { id, memberId } = useParams<{ id: string; memberId?: string }>();
  const familyId = parseInt(id || '0', 10);
  const isEdit   = Boolean(memberId);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(isEdit);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [phoneErr, setPhoneErr] = useState('');
  const [emailErr, setEmailErr] = useState('');

  const [name,            setName]            = useState('');
  const [relationship,    setRelationship]    = useState('Son');
  const [gender,          setGender]          = useState('Male');
  const [status,          setStatus]          = useState('Active');
  const [dob,             setDob]             = useState('');
  const [phone,           setPhone]           = useState('');
  const [email,           setEmail]           = useState('');
  const [occupation,      setOccupation]      = useState('');
  const [qualification,   setQualification]   = useState('');
  const [bloodGroup,      setBloodGroup]      = useState('');
  const [marriageDate,    setMarriageDate]    = useState('');
  const [baptizedDate,    setBaptizedDate]    = useState('');
  const [confirmationDate,    setConfirmationDate]    = useState('');
  const [firstCommunionDate,  setFirstCommunionDate]  = useState('');
  const [priesthoodDate,      setPriesthoodDate]      = useState('');
  const [memberGroup,     setMemberGroup]     = useState('');
  const [childClass,      setChildClass]      = useState('');
  const [childInstitution, setChildInstitution] = useState('');
  const [activities, setActivities] = useState<Record<string, boolean>>({});

  const toggleActivity = (key: string) => setActivities((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    if (!isEdit || !familyId || !memberId) return;
    setLoading(true);
    listFamilyMembers(familyId)
      .then((members) => {
        const m = members.find((row) => String(pick(row, ['memberId', 'member_id', 'MemberId', 'MemberID'])) === memberId);
        if (!m) { setError('Member not found.'); return; }
        setName(pick(m, ['name', 'Name']));
        setRelationship(pick(m, ['relationship', 'Relationship']) || 'Son');
        setGender(pick(m, ['gender', 'Gender']) || 'Male');
        setStatus(pick(m, ['member_status', 'status', 'Status']) || 'Active');
        setDob(pick(m, ['dob', 'DOB']).slice(0, 10));
        setPhone(pick(m, ['phone', 'Phone']));
        setEmail(pick(m, ['email', 'Email']));
        setOccupation(pick(m, ['occupation', 'Occupation']));
        setQualification(pick(m, ['qualification', 'Qualification']));
        setBloodGroup(pick(m, ['bloodGroup', 'BloodGroup', 'blood_group']));
        setMarriageDate(pick(m, ['marriageDate', 'MarriageDate']).slice(0, 10));
        setBaptizedDate(pick(m, ['baptismDate', 'BaptismDate']).slice(0, 10));
        setConfirmationDate(pick(m, ['confirmationDate', 'confirmation']).slice(0, 10));
        setFirstCommunionDate(pick(m, ['communionDate', 'Communion']).slice(0, 10));
        setPriesthoodDate(pick(m, ['priesthoodDate', 'Priesthood']).slice(0, 10));
        setMemberGroup(pick(m, ['memberGroup', 'MemberGroup']));
        setChildClass(pick(m, ['childClass', 'ChildClass']));
        setChildInstitution(pick(m, ['childInstitution', 'ChildInstitution']));

        const acts: Record<string, boolean> = {};
        for (const f of ACTIVITY_FIELDS) acts[f.key] = pickBool(m, f.rowKeys);
        setActivities(acts);
      })
      .catch(() => setError('Failed to load member.'))
      .finally(() => setLoading(false));
  }, [familyId, memberId, isEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Run all validations before submitting
    const pErr = validatePhone(phone);
    const eErr = validateEmail(email);
    setPhoneErr(pErr);
    setEmailErr(eErr);
    if (!name.trim())         { setError('Name is required'); return; }
    if (!relationship.trim()) { setError('Relationship is required'); return; }
    if (pErr || eErr)         { setError('Please fix the errors above before saving.'); return; }

    const payload: FamilyMemberPayload = {
      name: name.trim(), relationship, gender, status, dob: dob || null,
      phone, email, occupation, qualification, bloodGroup,
      marriageDate: marriageDate || null,
      baptizedDate: baptizedDate || null,
      confirmationDate: confirmationDate || null,
      firstCommunionDate: firstCommunionDate || null,
      priesthoodDate: priesthoodDate || null,
      memberGroup, childClass, childInstitution,
      ...activities,
    };

    setSaving(true);
    try {
      if (isEdit && memberId) {
        await updateFamilyMember(familyId, parseInt(memberId, 10), payload);
      } else {
        await createFamilyMember(familyId, payload);
      }
      navigate(`/families/${familyId}`);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!memberId) return;
    if (!window.confirm('Delete this member from the family? This cannot be undone.')) return;
    setSaving(true);
    try {
      await deleteFamilyMember(familyId, parseInt(memberId, 10));
      navigate(`/families/${familyId}`);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Delete failed.');
      setSaving(false);
    }
  };

  if (loading) return <div className="p-4 md:p-6 text-teal text-sm">Loading…</div>;

  const isChild = ['Son', 'Daughter'].includes(relationship);

  return (
    <div className="p-4 md:p-6 max-w-3xl">
      <Link to={`/families/${familyId}`} className="text-teal text-xs hover:underline">&larr; Back to Family</Link>
      <h1 className="text-xl font-semibold text-navy font-georgia mt-1 mb-6">
        {isEdit ? 'Edit Family Member' : 'Add Family Member'}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-300 text-red-700 text-sm rounded px-4 py-2">{error}</div>
        )}

        <section className="bg-white rounded-lg border border-grid-border shadow-sm p-6">
          <h2 className="text-sm font-semibold text-navy font-georgia mb-4">Personal Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Name</label>
              <input className="input-field" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div>
              <label className="label">Relationship</label>
              <select className="input-field" value={relationship} onChange={(e) => setRelationship(e.target.value)} required>
                {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Gender</label>
              <select className="input-field" value={gender} onChange={(e) => setGender(e.target.value)}>
                {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Status</label>
              <select className="input-field" value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Date of Birth</label>
              <input type="date" className="input-field" value={dob} onChange={(e) => setDob(e.target.value)} />
            </div>
            <div>
              <label className="label">Phone</label>
              <input
                className={`input-field ${phoneErr ? 'border-red-400' : ''}`}
                value={phone}
                maxLength={10}
                onChange={(e) => { setPhone(e.target.value); if (phoneErr) setPhoneErr(validatePhone(e.target.value)); }}
                onBlur={(e) => setPhoneErr(validatePhone(e.target.value))}
                placeholder="10-digit mobile"
              />
              <FieldError msg={phoneErr} />
            </div>
            <div>
              <label className="label">Email</label>
              <input
                type="text"
                className={`input-field ${emailErr ? 'border-red-400' : ''}`}
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (emailErr) setEmailErr(validateEmail(e.target.value)); }}
                onBlur={(e) => setEmailErr(validateEmail(e.target.value))}
                placeholder="user@example.com"
              />
              <FieldError msg={emailErr} />
            </div>
            <div>
              <label className="label">Occupation</label>
              <input className="input-field" value={occupation} onChange={(e) => setOccupation(e.target.value)} />
            </div>
            <div>
              <label className="label">Qualification</label>
              <input className="input-field" value={qualification} onChange={(e) => setQualification(e.target.value)} />
            </div>
            <div>
              <label className="label">Blood Group</label>
              <select className="input-field" value={bloodGroup} onChange={(e) => setBloodGroup(e.target.value)}>
                <option value="">—</option>
                {BLOOD_GROUPS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>
        </section>

        {isChild && (
          <section className="bg-white rounded-lg border border-grid-border shadow-sm p-6">
            <h2 className="text-sm font-semibold text-navy font-georgia mb-4">Education</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Class</label>
                <input className="input-field" value={childClass} onChange={(e) => setChildClass(e.target.value)} />
              </div>
              <div>
                <label className="label">Institution</label>
                <input className="input-field" value={childInstitution} onChange={(e) => setChildInstitution(e.target.value)} />
              </div>
            </div>
          </section>
        )}

        <section className="bg-white rounded-lg border border-grid-border shadow-sm p-6">
          <h2 className="text-sm font-semibold text-navy font-georgia mb-4">Sacraments &amp; Milestones</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Baptism Date</label>
              <input type="date" className="input-field" value={baptizedDate} onChange={(e) => setBaptizedDate(e.target.value)} />
            </div>
            <div>
              <label className="label">First Communion Date</label>
              <input type="date" className="input-field" value={firstCommunionDate} onChange={(e) => setFirstCommunionDate(e.target.value)} />
            </div>
            <div>
              <label className="label">Confirmation Date</label>
              <input type="date" className="input-field" value={confirmationDate} onChange={(e) => setConfirmationDate(e.target.value)} />
            </div>
            <div>
              <label className="label">Marriage Date</label>
              <input type="date" className="input-field" value={marriageDate} onChange={(e) => setMarriageDate(e.target.value)} />
            </div>
            <div>
              <label className="label">Priesthood / Religious Vows Date</label>
              <input type="date" className="input-field" value={priesthoodDate} onChange={(e) => setPriesthoodDate(e.target.value)} />
            </div>
            <div>
              <label className="label">Member Group</label>
              <input className="input-field" value={memberGroup} onChange={(e) => setMemberGroup(e.target.value)} />
            </div>
          </div>
        </section>

        <section className="bg-white rounded-lg border border-grid-border shadow-sm p-6">
          <h2 className="text-sm font-semibold text-navy font-georgia mb-4">Parish Activities &amp; Ministries</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {ACTIVITY_FIELDS.map((f) => (
              <label key={f.key} className="flex items-center gap-2 text-sm text-navy">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={!!activities[f.key]}
                  onChange={() => toggleActivity(f.key)}
                />
                {f.label}
              </label>
            ))}
          </div>
        </section>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
          <div>
            {isEdit && (
              <button type="button" onClick={handleRemove} disabled={saving} className="btn-danger disabled:opacity-50 w-full sm:w-auto">
                Delete Member
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => navigate(-1)} className="btn-secondary flex-1 sm:flex-initial">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50 flex-1 sm:flex-initial">
              {saving ? 'Saving…' : isEdit ? 'Update Member' : 'Add Member'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
